import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents, Tooltip, Polygon, ZoomControl } from 'react-leaflet';
import L  from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, IconButton, Tooltip as MuiTooltip, Typography, Divider, Menu, MenuItem, Checkbox, Button, Dialog, DialogContent, DialogActions, useTheme, Popover } from '@mui/material';
import LabelIcon from '@mui/icons-material/Label';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import HistoryIcon from '@mui/icons-material/History';
import CropFreeIcon from '@mui/icons-material/CropFree';
import { GeographicArea } from 'org-graph/geographicSelection';
import MapAreaSelection from './MapAreaSelection';
import { Profile } from '../Routes/CompanyAssessment/types';
import { iconMap, colorMap } from 'org-graph/GraphVizConstants';
import { NodeLabelSettings, EdgeLabelSettings } from 'org-graph/types';
import { Documentation, parseCoordinates } from 'org-graph';
import TimeSliderPanel from 'org-graph/TimeSliderPanel';
import CompanyMapSidePanel, { MapListEntry, HistoryEntry } from './CompanyMapSidePanel';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

// Fix for default marker icons in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface CompanyLocation {
  companyId: string;
  companyName: string;
  companyData: any; // Full companpy node data for icon styling
  isSourceCompany: boolean; // Whether this is the company being searched
  isHidden?: boolean;
  currentAddress: {
    id: string;
    coordinates: [number, number];
    addressText: string;
    startDate?: string;
    endDate?: string;
    isHidden?: boolean;
  };
  historicalAddresses: Array<{
    id: string;
    coordinates: [number, number];
    addressText: string;
    startDate?: string;
    endDate?: string;
    isHidden?: boolean;
  }>;
}

interface CompanyMapCardProps {
  profile: Profile;
  hiddenNodeIds?: Set<string>;
  isFullscreen?: boolean;
  onToggleFullscreen?: (event: React.MouseEvent) => void;
  selectedDate?: string | null;
  onDateChange?: (date: string | null) => void;
  geographicArea?: GeographicArea | null;
  geographicCompanyIds?: ReadonlySet<string> | null;
  onGeographicAreaChange?: (area: GeographicArea | null) => void;
}

// Helper function to calculate icon size based on zoom level
// Zoom levels typically range from 1 (world view) to 18+ (street level)
// We'll scale from 50% size at zoom 6 to 100% size at zoom 13+
const calculateIconSize = (baseSize: number, zoomLevel: number): number => {
  const minZoom = 6;
  const maxZoom = 13;
  const minScale = 0.5; // 50% of base size at minimum zoom
  const maxScale = 1.0; // 100% size at maximum zoom
  
  if (zoomLevel <= minZoom) return baseSize * minScale;
  if (zoomLevel >= maxZoom) return baseSize * maxScale;
  
  // Linear interpolation between min and max zoom
  const scale = minScale + (maxScale - minScale) * ((zoomLevel - minZoom) / (maxZoom - minZoom));
  return Math.round(baseSize * scale);
};

// Helper function to determine company icon styling based on properties
const getCompanyIconConfig = (company: any, isSourceCompany: boolean) => {
  
  // Source company (the one being searched) always gets green
  if (isSourceCompany) {
    return {
      shapeColor: iconMap.Company.starting.shapeColor,
      iconColor: iconMap.Company.starting.color,
      iconCode: iconMap.Company.starting.code,
      size: 40
    };
  }

  // Check for debt
  if (company.properties?.amal_skuldsaldo_current || company.properties?.emal_skuldsaldo_current) {
    return {
      shapeColor: iconMap.Company.withDebt.shapeColor,
      iconColor: iconMap.Company.withDebt.color,
      iconCode: iconMap.Company.withDebt.code,
      size: 40
    };
  }

  // Check for bankruptcy
  if (company.properties?.konkurs_inledd && !company.properties?.konkurs_avslutad) {
    return {
      shapeColor: iconMap.Company.withBankrupcy.shapeColor,
      iconColor: iconMap.Company.withBankrupcy.color,
      iconCode: iconMap.Company.withBankrupcy.code,
      size: 40
    };
  }

  // Check for liquidation
  if (company.properties?.likvidation_beslutad && !company.properties?.likvidation_avslutad) {
    return {
      shapeColor: iconMap.Company.withLikvidation.shapeColor,
      iconColor: iconMap.Company.withLikvidation.color,
      iconCode: iconMap.Company.withLikvidation.code,
      size: 40
    };
  }

  // Default company icon
  return {
    shapeColor: iconMap.Company.default.shapeColor,
    iconColor: iconMap.Company.default.color,
    iconCode: iconMap.Company.default.code,
    size: 40
  };
};

// Create custom icon for company (building icon with styling from GraphVizConstants)
const createCompanyIcon = (company: any, isSourceCompany: boolean, zoomLevel: number = 10, labels?: number[]) => {
  const config = getCompanyIconConfig(company, isSourceCompany);
  const size = calculateIconSize(config.size, zoomLevel);
  const iconFontSize = Math.max(12, Math.round(size * 0.5)); // Scale font size proportionally
  
  const badgesHtml = labels ? labels.map((num, index) => {
    // Position badges around the icon
    let style = '';
    if (index === 0) style = 'top: -10px; right: -10px;';
    else if (index === 1) style = 'top: -10px; left: -10px;';
    else if (index === 2) style = 'bottom: -10px; right: -10px;';
    else if (index === 3) style = 'bottom: -10px; left: -10px;';
    else return ''; // Limit to 4 badges to avoid clutter

    return `<div style="
      position: absolute;
      ${style}
      background-color: #ef4444;
      color: white;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      font-weight: 800;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      z-index: 10;
      font-family: var(--font-family);
    ">${num}</div>`;
  }).join('') : '';

  // Modern, flat design with subtle shadow
  return L.divIcon({
    html: `<div style="position: relative;">
      <div style="
        background-color: ${config.shapeColor};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px solid #fff;
        box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        transition: all 0.3s ease;
      ">
        <i class="fas ${config.iconCode === '\uf1ad' ? 'fa-building' : 'fa-building'}" style="
          color: ${config.iconColor};
          font-size: ${iconFontSize}px;
          font-family: 'Font Awesome 5 Free';
          font-weight: 900;
        "></i>
      </div>
      ${badgesHtml}
    </div>`,
    className: 'custom-company-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
};

// Create custom icon for historical address (dot style)
const createHistoricalIcon = (zoomLevel: number = 10, labels?: number[]) => {
  const baseSize = 16; // Smaller, more subtle
  const size = calculateIconSize(baseSize, zoomLevel);
  
  const badgesHtml = labels ? labels.map((num, index) => {
    // Position badges around the icon
    let style = '';
    if (index === 0) style = 'top: -12px; right: -12px;';
    else if (index === 1) style = 'top: -12px; left: -12px;';
    else if (index === 2) style = 'bottom: -12px; right: -12px;';
    else if (index === 3) style = 'bottom: -12px; left: -12px;';
    else return ''; 

    return `<div style="
      position: absolute;
      ${style}
      background-color: #ef4444;
      color: white;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      font-weight: 800;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      z-index: 10;
      font-family: var(--font-family);
    ">${num}</div>`;
  }).join('') : '';

  return L.divIcon({
    html: `<div style="position: relative;">
      <div style="
        background-color: ${colorMap.shapeColor.Address};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        border: 2px solid #fff;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        display: flex;
        align-items: center;
        justify-content: center;
      "></div>
      ${badgesHtml}
    </div>`,
    className: 'custom-historical-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
};

// Helper function to calculate a position offset in meters (dx = East, dy = North)
const calculateOffsetInMeters = (
  origin: [number, number],
  dx: number,
  dy: number
): [number, number] => {
  const earthRadius = 6378137;
  const [lat, lng] = origin;
  const dLat = (dy / earthRadius) * (180 / Math.PI);
  const dLng = (dx / (earthRadius * Math.cos(lat * Math.PI / 180))) * (180 / Math.PI);
  return [lat + dLat, lng + dLng];
};

// Helper to calculate bearing between two points
const calculateBearing = (start: [number, number], end: [number, number]): number => {
  const toRad = (deg: number) => deg * Math.PI / 180;
  const toDeg = (rad: number) => rad * 180 / Math.PI;

  const startLat = toRad(start[0]);
  const startLng = toRad(start[1]);
  const endLat = toRad(end[0]);
  const endLng = toRad(end[1]);

  const y = Math.sin(endLng - startLng) * Math.cos(endLat);
  const x = Math.cos(startLat) * Math.sin(endLat) -
            Math.sin(startLat) * Math.cos(endLat) * Math.cos(endLng - startLng);
  const brng = toDeg(Math.atan2(y, x));
  return (brng + 360) % 360;
};

// Helper to create an arrow icon
const createArrowIcon = (bearing: number, color: string = '#222') => {
  return L.divIcon({
    html: `<div style="transform: rotate(${bearing}deg); display: flex; align-items: center; justify-content: center;">
      <i class="fas fa-arrow-up" style="color: ${color}; font-size: 16px; text-shadow: 0 0 3px #fff, 0 0 3px #fff;"></i>
    </div>`,
    className: 'arrow-icon',
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });
};

// Helper function to create a coordinate key for grouping
const getCoordinateKey = (coords: [number, number]): string => {
  return `${coords[0].toFixed(6)},${coords[1].toFixed(6)}`;
};

// Interface for markers that can be positioned
interface PositionableMarker {
  id: string;
  type: 'company' | 'person';
  originalCoordinates: [number, number];
  displayCoordinates: [number, number];
  data: any;
}

// Component to fit map bounds to show all markers
const FitBounds: React.FC<{ 
  companyLocations: CompanyLocation[];
  area: GeographicArea | null;
}> = ({ companyLocations, area }) => {
  const map = useMap();
  const prevBoundsRef = useRef<string>('');

  useEffect(() => {
    if (companyLocations.length === 0) return;

    // Collect all coordinates
    const allCoordinates: [number, number][] = [
      ...companyLocations.flatMap(loc => [
        loc.currentAddress.coordinates,
        ...loc.historicalAddresses.map(h => h.coordinates)
      ])
    ];

    if (allCoordinates.length > 0) {
      // Create bounds from all coordinates
      const bounds = area ? L.latLngBounds([area.south, area.west], [area.north, area.east]) : L.latLngBounds(allCoordinates);
      const boundsKey = bounds.toBBoxString();
      
      // Only fit bounds if they have changed
      if (boundsKey !== prevBoundsRef.current) {
        map.fitBounds(bounds, { padding: [20, 20], maxZoom: 16 });
        prevBoundsRef.current = boundsKey;
      }
    }
  }, [companyLocations, map, area]);

  return null;
};

// Component to handle zoom level changes
const ZoomHandler: React.FC<{ onZoomChange: (zoom: number) => void }> = ({ onZoomChange }) => {
  const map = useMap();

  useEffect(() => {
    const handleZoom = () => {
      onZoomChange(map.getZoom());
    };

    // Set initial zoom
    onZoomChange(map.getZoom());

    // Listen for zoom changes
    map.on('zoomend', handleZoom);

    return () => {
      map.off('zoomend', handleZoom);
    };
  }, [map, onZoomChange]);

  return null;
};

// Component to handle map resizing
const MapResizer: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });
    
    const container = map.getContainer();
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [map]);

  return null;
};

// Component to handle map events
const MapEventHandler: React.FC<{ onInteraction: () => void }> = ({ onInteraction }) => {
  useMapEvents({
    dragstart: onInteraction,
    zoomstart: onInteraction,
    click: onInteraction
  });
  return null;
};

// Component to handle map flyTo actions
const MapFlyTo: React.FC<{ 
  target: { coordinates: [number, number], zoom: number } | null;
  onFlyComplete?: () => void;
}> = ({ target, onFlyComplete }) => {
  const map = useMap();

  useEffect(() => {
    if (target) {
      map.flyTo(target.coordinates, target.zoom, {
        duration: 1.5,
        easeLinearity: 0.25
      });

      if (onFlyComplete) {
        onFlyComplete();
      }
    }
  }, [target, map, onFlyComplete]);

  return null;
};

const CompanyMapCard: React.FC<CompanyMapCardProps> = ({ profile, hiddenNodeIds, isFullscreen = false, onToggleFullscreen, selectedDate, onDateChange, geographicArea = null, geographicCompanyIds, onGeographicAreaChange }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const cartoBasemapApiKey = process.env.REACT_APP_CARTO_BASEMAPS_API_KEY;
  const cartoBasemapUrl = cartoBasemapApiKey
    ? `https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(cartoBasemapApiKey)}`
    : 'https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}{r}.png';
  // State to control label visibility
  const [showLabels, setShowLabels] = useState<boolean>(true); // Master toggle
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const [showHistory, setShowHistory] = useState(false);
  const [drawingArea, setDrawingArea] = useState(false);
  const cancelDrawing = useCallback(() => setDrawingArea(false), []);
  
  // Granular label settings
  const [nodeLabelSettings, setNodeLabelSettings] = useState<NodeLabelSettings>({
    showOrganizationNumber: false,
    showOrganizationName: false,
    showKonkursLikvidation: false,
    showSkuldsaldo: false,
    showAddress: false,
    showName: false
  });

  const [edgeLabelSettings, setEdgeLabelSettings] = useState<EdgeLabelSettings>({
    showRole: false,
    showDateRange: false,
    showDuration: false
  });

  const [labelAnchorEl, setLabelAnchorEl] = useState<null | HTMLElement>(null);
  const [timeSliderAnchorEl, setTimeSliderAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLocation, setSelectedLocation] = useState<{ coordinates: [number, number], zoom: number } | null>(null);
  const [hoveredCoordinates, setHoveredCoordinates] = useState<[number, number] | null>(null);

  // Street View State
  const [streetViewOpen, setStreetViewOpen] = useState(false);
  const [streetViewLocation, setStreetViewLocation] = useState<{ lat: number, lng: number } | null>(null);

  const handleLabelClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setLabelAnchorEl(event.currentTarget);
  };

  const handleLabelClose = () => {
    setLabelAnchorEl(null);
  };

  const handleTimeSliderClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setTimeSliderAnchorEl(event.currentTarget);
  };

  const handleTimeSliderClose = () => {
    setTimeSliderAnchorEl(null);
  };

  const openTimeSlider = Boolean(timeSliderAnchorEl);
  const timeSliderId = openTimeSlider ? 'time-slider-popover' : undefined;

  const handleNodeLabelToggle = (key: keyof NodeLabelSettings) => {
    setNodeLabelSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setShowLabels(true);
  };

  const handleEdgeLabelToggle = (key: keyof EdgeLabelSettings) => {
    setEdgeLabelSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setShowLabels(true);
  };

  // State to track map zoom level for dynamic icon sizing
  const [zoomLevel, setZoomLevel] = useState<number>(10);
  const rootRef = useRef<HTMLDivElement>(null);

  // Calculate min and max dates from all relationships
  const { minDate, maxDate } = useMemo(() => {
    let min = '';
    // Max date is always current date
    const max = new Date().toISOString().split('T')[0];
    const relationships = profile.graphData.relationships;
    const sourceNodeId = profile.graphData.metadata?.sourceNodeId;
    
    if (sourceNodeId) {
      relationships.forEach((edge: any) => {
        // Calculate min date ONLY from Source Node relationships
        const isSourceEdge = edge.from === sourceNodeId || edge.to === sourceNodeId;
        if (isSourceEdge) {
          if (edge.properties.start_date) {
            if (!min || edge.properties.start_date < min) min = edge.properties.start_date;
          }
          if (edge.properties.end_date) {
            if (!min || edge.properties.end_date < min) min = edge.properties.end_date;
          }
        }
      });
    }
    
    // If no min date found, return nulls
    if (!min) return { minDate: null, maxDate: null };
    
    return { minDate: min, maxDate: max };
  }, [profile.graphData]);

  const companyLocations = useMemo(() => {
    const locations: CompanyLocation[] = [];
    const nodes = profile.graphData.nodes;
    const relationships = profile.graphData.relationships;
    const graphMetaData = profile.graphData.metadata;

    // Find all Company nodes
    const companyNodes = nodes.filter((node: any) => node.type === 'Company');

    companyNodes.forEach((company: any) => {
      if (geographicCompanyIds && !geographicCompanyIds.has(company.id)) return;
      // Check if company is hidden
      let isHidden = hiddenNodeIds ? hiddenNodeIds.has(company.id) : false;

      // Find all address relationships for this company
      // Check if rel.from is the company and rel.to is an Address node
      let addressRelationships = relationships.filter((rel: any) => {
        if (rel.from !== company.id && rel.to !== company.id) return false;
        
        // Must go to an Address node
        const toNode = nodes.find((node: any) => node.id === (rel.from === company.id ? rel.to : rel.from));
        return toNode && toNode.type === 'Address';
      });

      // Filter by selectedDate if provided
      if (selectedDate) {
        addressRelationships = addressRelationships.filter((rel: any) => {
           const startDate = rel.properties?.start_date;
           if (startDate && String(startDate) > String(selectedDate)) return false;
           return true;
        }).map((rel: any) => {
           // Always create a shallow copy to avoid mutating the original props
           const newRel = { ...rel, properties: { ...rel.properties } };
           
           const endDate = newRel.properties?.end_date;
           // If it ends after selectedDate, treat it as current (no end date) and active
           if (endDate && String(endDate) > String(selectedDate)) {
             newRel.properties.end_date = undefined;
             newRel.properties.is_active = true;
           } else if (endDate && String(endDate) <= String(selectedDate)) {
             // If it ends before or on selectedDate, it's historical (inactive)
             newRel.properties.is_active = false;
           }
           
           return newRel;
        });
      }

      if (addressRelationships.length === 0) {
        return;
      }

      // Separate current and historical addresses
      const date = selectedDate || new Date().toISOString().slice(0, 10);
      const activeAddressRels = addressRelationships.filter((rel: any) => rel.properties?.is_active !== false
        && (!rel.properties?.start_date || rel.properties.start_date.slice(0, 10) <= date)
        && (!rel.properties?.end_date || rel.properties.end_date.slice(0, 10) > date));
      // Prefer an active address in the selected area when a company has several addresses.
      const currentAddressRel = (geographicArea && activeAddressRels.find((rel: any) => {
        const coords = parseCoordinates(nodes.find((node: any) => node.id === (rel.from === company.id ? rel.to : rel.from))?.properties);
        return coords && coords[0] >= geographicArea.south && coords[0] <= geographicArea.north
          && coords[1] >= geographicArea.west && coords[1] <= geographicArea.east;
      })) || activeAddressRels[0];
      if (!currentAddressRel) return;

      const historicalAddressRels = addressRelationships.filter(
        (rel: any) => !activeAddressRels.includes(rel)
      );

      // Get the current address node
      const getCurrentAddressNode = () => {
        if (!currentAddressRel) return null;
        const addressId = currentAddressRel.from === company.id 
          ? currentAddressRel.to 
          : currentAddressRel.from;
        return nodes.find((node: any) => node.id === addressId && node.type === 'Address');
      };

      const currentAddressNode = getCurrentAddressNode();
      
      // Check if current address is hidden
      if (currentAddressNode && hiddenNodeIds && hiddenNodeIds.has(currentAddressNode.id)) {
        isHidden = true;
      }
      
      // Only add to locations if current address exists and has coordinates
      if (currentAddressNode) {
        const currentCoords = parseCoordinates(currentAddressNode.properties);
        
        if (currentCoords) {
          const [lat, lng] = currentCoords;
          
          // Get historical addresses with coordinates
          const historicalAddresses = historicalAddressRels
            .map((rel: any) => {
              const addressId = rel.from === company.id ? rel.to : rel.from;
              const addressNode = nodes.find((node: any) => node.id === addressId && node.type === 'Address');
              return { node: addressNode, relationship: rel };
            })
            .filter((item: any) => {
              // Check if historical address is hidden
              // We now include hidden addresses so they can be shown in the list with an icon
              // if (item.node && hiddenNodeIds && hiddenNodeIds.has(item.node.id)) {
              //   return false;
              // }
              return item.node;
            })
            .map((item: any) => {
              const histCoords = parseCoordinates(item.node!.properties);
              if (!histCoords) return null;
              
              return {
                id: item.node!.id,
                coordinates: histCoords,
                addressText: formatAddress(item.node!.properties, t),
                startDate: item.relationship?.properties?.start_date,
                endDate: item.relationship?.properties?.end_date,
                isHidden: hiddenNodeIds?.has(item.node!.id)
              };
            })
            .filter((item: any) => item !== null) as CompanyLocation['historicalAddresses'];

          locations.push({
            companyId: company.id,
            companyName: company.name,
            companyData: company, // Store full company data for icon styling
            isSourceCompany: company.id === graphMetaData.sourceNodeId, // Check if this is the searched company
            isHidden,
            currentAddress: {
              id: currentAddressNode.id,
              coordinates: [lat, lng],
              addressText: formatAddress(currentAddressNode.properties, t),
              startDate: currentAddressRel?.properties?.start_date,
              endDate: currentAddressRel?.properties?.end_date,
              isHidden: hiddenNodeIds?.has(currentAddressNode.id)
            },
            historicalAddresses: showHistory ? historicalAddresses : []
          });
        }
      }
    });

    return locations;
  }, [profile.graphData, hiddenNodeIds, selectedDate, t, showHistory, geographicArea, geographicCompanyIds]);

  const [selectedEntity, setSelectedEntity] = useState<{ id: string, type: 'Company' | 'Address' } | null>(null);
  const [filterEnabled, setFilterEnabled] = useState<boolean>(false);
  const [showConnections, setShowConnections] = useState<boolean>(false);
  
  const filteredCompanyLocations = useMemo(() => {
    if (!filterEnabled || !selectedEntity) {
      return companyLocations;
    }

    if (selectedEntity.type === 'Company') {
        const selectedLocation = companyLocations.find(loc => loc.companyId === selectedEntity.id);
        if (!selectedLocation) {
            return companyLocations;
        }

        if (!showConnections) {
            return [selectedLocation];
        }

        // Get all address IDs (current and historical) for the selected company
        const selectedAddressIds = new Set<string>();
        selectedAddressIds.add(selectedLocation.currentAddress.id);
        selectedLocation.historicalAddresses.forEach(h => selectedAddressIds.add(h.id));

        // Filter locations
        return companyLocations.filter(loc => {
            // Check current address
            if (selectedAddressIds.has(loc.currentAddress.id)) return true;
            // Check historical addresses
            if (loc.historicalAddresses.some(h => selectedAddressIds.has(h.id))) return true;
            return false;
        });
    } else if (selectedEntity.type === 'Address') {
        // Filter locations that have this address as current or historical
        return companyLocations.filter(loc => {
            if (loc.currentAddress.id === selectedEntity.id) return true;
            if (loc.historicalAddresses.some(h => h.id === selectedEntity.id)) return true;
            return false;
        });
    }
    
    return companyLocations;
  }, [companyLocations, selectedEntity, filterEnabled, showConnections]);

  // Create positioned markers with grid layout for overlapping locations
  const { markers: positionedMarkers, groups: markerGroups } = useMemo(() => {
    const markers: PositionableMarker[] = [];
    const groups: {
      id: string;
      center: [number, number];
      gridTopCenter: [number, number];
      bounds: [number, number][];
    }[] = [];
    
    // Add all company current addresses as markers
    filteredCompanyLocations.forEach(location => {
      if (location.isHidden) return;
      markers.push({
        id: location.companyId,
        type: 'company',
        originalCoordinates: location.currentAddress.coordinates,
        displayCoordinates: location.currentAddress.coordinates, // Will be adjusted if overlapping
        data: location
      });
    });
    
    // Group markers by coordinate
    const coordGroups = new Map<string, PositionableMarker[]>();
    markers.forEach(marker => {
      const key = getCoordinateKey(marker.originalCoordinates);
      if (!coordGroups.has(key)) {
        coordGroups.set(key, []);
      }
      coordGroups.get(key)!.push(marker);
    });
    
    // Apply grid layout to overlapping markers
    coordGroups.forEach((group, coordKey) => {
      if (group.length > 1) {
        // Grid layout settings
        const itemsPerRow = 5;
        const spacing = 35; // meters between icons
        const groupOffset = 80; // meters (distance from address to top of grid)
        
        const numRows = Math.ceil(group.length / itemsPerRow);
        const maxCols = Math.min(group.length, itemsPerRow);
        
        const gridWidth = (maxCols - 1) * spacing;
        const gridHeight = (numRows - 1) * spacing;
        
        const addressCoords = group[0].originalCoordinates;
        
        // Calculate grid positions
        group.forEach((marker, index) => {
          const row = Math.floor(index / itemsPerRow);
          const col = index % itemsPerRow;
          
          // Center the grid horizontally
          const dx = (-gridWidth / 2) + (col * spacing);
          const dy = -groupOffset - (row * spacing); // Negative dy is South
          
          marker.displayCoordinates = calculateOffsetInMeters(addressCoords, dx, dy);
        });

        // Calculate bounding box for outline
        const padding = 15; // meters
        const topY = -groupOffset + padding;
        const bottomY = -groupOffset - gridHeight - padding;
        const leftX = -gridWidth/2 - padding;
        const rightX = gridWidth/2 + padding;
        
        const topLeft = calculateOffsetInMeters(addressCoords, leftX, topY);
        const topRight = calculateOffsetInMeters(addressCoords, rightX, topY);
        const bottomRight = calculateOffsetInMeters(addressCoords, rightX, bottomY);
        const bottomLeft = calculateOffsetInMeters(addressCoords, leftX, bottomY);
        
        // Point where the line connects to the grid (top center of bounding box)
        const gridTopCenter = calculateOffsetInMeters(addressCoords, 0, topY);

        groups.push({
            id: coordKey,
            center: addressCoords,
            gridTopCenter: gridTopCenter,
            bounds: [topLeft, topRight, bottomRight, bottomLeft]
        });
      }
      // If only 1 marker, displayCoordinates stays the same as originalCoordinates
    });
    
    return { markers, groups };
  }, [filteredCompanyLocations]);

  // Calculate center of map based on all coordinates
  const mapCenter = useMemo(() => {
    if (filteredCompanyLocations.length === 0) {
      return [59.3293, 18.0686] as [number, number]; // Default to Stockholm
    }

    const allLats = [
      ...filteredCompanyLocations.flatMap(loc => [
        loc.currentAddress.coordinates[0],
        ...loc.historicalAddresses.map(h => h.coordinates[0])
      ])
    ];
    const allLngs = [
      ...filteredCompanyLocations.flatMap(loc => [
        loc.currentAddress.coordinates[1],
        ...loc.historicalAddresses.map(h => h.coordinates[1])
      ])
    ];

    const centerLat = allLats.reduce((a, b) => a + b, 0) / allLats.length;
    const centerLng = allLngs.reduce((a, b) => a + b, 0) / allLngs.length;

    return [centerLat, centerLng] as [number, number];
  }, [filteredCompanyLocations]);

  // Check if we are in the specific "single company path" mode
  const isSingleCompanyPathMode = showHistory && filterEnabled && selectedEntity?.type === 'Company' && !showConnections;

  // Calculate address order if in single company mode
  const addressOrderMap = useMemo(() => {
    const map = new Map<string, number[]>(); // Coordinate Key -> [Indices]
    
    if (isSingleCompanyPathMode && filteredCompanyLocations.length === 1) {
        const location = filteredCompanyLocations[0];
        // Same sort logic as in the render loop
        const sorted = [
            ...location.historicalAddresses.map(h => ({ ...h, id: h.id, coordinates: h.coordinates, endDate: h.endDate })),
            { 
                id: location.currentAddress.id, 
                coordinates: location.currentAddress.coordinates, 
                endDate: location.currentAddress.endDate,
            }
        ].sort((a, b) => {
             const dateA = a.endDate || '9999-12-31';
             const dateB = b.endDate || '9999-12-31';
             return dateA.localeCompare(dateB);
        });
        
        sorted.forEach((addr, index) => {
            const key = getCoordinateKey(addr.coordinates);
            if (!map.has(key)) {
                map.set(key, []);
            }
            map.get(key)!.push(index + 1);
        });
    }
    return map;
  }, [isSingleCompanyPathMode, filteredCompanyLocations]);

    // Group historical addresses by coordinate to avoid overlapping markers
  const groupedHistoricalMarkers = useMemo(() => {
    const groups = new Map<string, {
      coordinates: [number, number];
      entries: Array<{
        companyName: string;
        addressText: string;
        startDate?: string;
        endDate?: string;
      }>;
    }>();

    filteredCompanyLocations.forEach(location => {
      if (location.isHidden) return;
      location.historicalAddresses.forEach(historical => {
        // Skip hidden addresses
        if (historical.isHidden) return;

        // Skip self-loops (historical == current)
        if (
          historical.coordinates[0] === location.currentAddress.coordinates[0] &&
          historical.coordinates[1] === location.currentAddress.coordinates[1]
        ) {
          return;
        }        const key = getCoordinateKey(historical.coordinates);
        if (!groups.has(key)) {
          groups.set(key, {
            coordinates: historical.coordinates,
            entries: []
          });
        }
        
        groups.get(key)!.entries.push({
          companyName: location.companyName,
          addressText: historical.addressText,
          startDate: historical.startDate,
          endDate: historical.endDate
        });
      });
    });

    return Array.from(groups.values());
  }, [filteredCompanyLocations]);

  // Calculate lists for side panel
  const { companiesList, addressesList } = useMemo(() => {
    const nodes = profile.graphData.nodes;
    const relationships = profile.graphData.relationships;

    // Helper to count connections
    const countConnections = (nodeId: string) => {
      return relationships.filter((rel: any) => rel.from === nodeId || rel.to === nodeId).length;
    };

    const companies: MapListEntry[] = [];
    const addresses: MapListEntry[] = [];

    // Process companies
    filteredCompanyLocations.forEach(loc => {
      const node = nodes.find((n: any) => n.id === loc.companyId);
      companies.push({
        id: loc.companyId,
        name: loc.companyName,
        organizationNumber: node?.properties?.organization_number,
        type: 'Company',
        connectionCount: countConnections(loc.companyId),
        next_layer_company_count: node?.properties?.next_layer_company_count,
        coordinates: loc.currentAddress.coordinates,
        isHidden: loc.isHidden
      });
    });

    // Process addresses (unique addresses from locations)
    const processedAddressIds = new Set<string>();
    
    // Add current addresses
    filteredCompanyLocations.forEach(loc => {
      if (!processedAddressIds.has(loc.currentAddress.id)) {
        addresses.push({
          id: loc.currentAddress.id,
          name: loc.currentAddress.addressText,
          type: 'Address',
          connectionCount: countConnections(loc.currentAddress.id),
          coordinates: loc.currentAddress.coordinates,
          isHidden: loc.currentAddress.isHidden
        });
        processedAddressIds.add(loc.currentAddress.id);
      }
      
      // Add historical addresses
      loc.historicalAddresses.forEach(hist => {
        if (!processedAddressIds.has(hist.id)) {
          addresses.push({
            id: hist.id,
            name: hist.addressText,
            type: 'Address',
            connectionCount: countConnections(hist.id),
            coordinates: hist.coordinates,
            isHidden: hist.isHidden
          });
          processedAddressIds.add(hist.id);
        }
      });
    });

    // Sort by connection count descending
    companies.sort((a, b) => b.connectionCount - a.connectionCount);
    addresses.sort((a, b) => b.connectionCount - a.connectionCount);

    return { companiesList: companies, addressesList: addresses };
  }, [profile.graphData, filteredCompanyLocations]);

  // Calculate history list for selected company
  const selectedCompanyHistory = useMemo(() => {
    if (!selectedEntity || selectedEntity.type !== 'Company') return undefined;
    
    const location = companyLocations.find(loc => loc.companyId === selectedEntity.id);
    if (!location) return undefined;

    const history: HistoryEntry[] = [
      ...location.historicalAddresses.map(h => ({
        addressText: h.addressText,
        startDate: h.startDate,
        endDate: h.endDate,
        coordinates: h.coordinates,
        isCurrent: false
      })),
      {
        addressText: location.currentAddress.addressText,
        startDate: location.currentAddress.startDate,
        endDate: location.currentAddress.endDate,
        coordinates: location.currentAddress.coordinates,
        isCurrent: true
      }
    ];

    // Sort by date
    return history.sort((a, b) => {
      const dateA = a.endDate || '9999-12-31';
      const dateB = b.endDate || '9999-12-31';
      return dateA.localeCompare(dateB);
    });
  }, [selectedEntity, companyLocations]);

  return (
    <Box ref={rootRef} sx={{ display: 'flex', height: '100%', width: '100%' }}>
      {/* Side Panel */}
      <CompanyMapSidePanel 
        companies={companiesList} 
        addresses={addressesList} 
        onSelectLocation={(coords) => setSelectedLocation({ coordinates: coords, zoom: 16 })}
        onHoverLocation={setHoveredCoordinates}
        selectedEntity={selectedEntity}
        onSelectEntity={setSelectedEntity}
        filterEnabled={filterEnabled}
        onToggleFilter={setFilterEnabled}
        showConnections={showConnections}
        onToggleShowConnections={setShowConnections}
        history={selectedCompanyHistory} // Pass the history to the side panel
        isCompact={!isFullscreen}
      />

      <Box sx={{ 
        flex: 1,
        height: '100%', 
        position: 'relative',
        '& .leaflet-tooltip.address-tooltip': {
        backgroundColor: 'rgba(30, 30, 30, 0.95)',
        border: '1px solid #444',
        borderRadius: '2px',
        padding: '4px 8px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.4)',
        fontSize: '11px',
        fontWeight: '500',
        fontFamily: 'var(--font-family)',
        color: '#eee',
      },
      '& .leaflet-tooltip.company-tooltip': {
        backgroundColor: 'rgba(255, 255, 255, 0.65)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
        borderRadius: '6px',
        padding: '4px 8px',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
        fontSize: '12px',
        fontWeight: '500',
        fontFamily: 'var(--font-family)',
        color: '#1a1a1a',
        maxWidth: '600px !important',
        width: 'max-content',
        whiteSpace: 'normal',
        letterSpacing: '0.2px',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      },
      '& .leaflet-tooltip.polyline-tooltip': {
        backgroundColor: 'rgba(40, 40, 40, 0.9)',
        border: '1px solid #666',
        borderRadius: '2px',
        padding: '3px 6px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        fontSize: '10px',
        fontWeight: '600',
        fontFamily: 'var(--font-family)',
        color: '#ccc',
      },
      '& .leaflet-tooltip-top:before, & .leaflet-tooltip-bottom:before, & .leaflet-tooltip-center:before': {
        display: 'none',
      },
      '& .leaflet-popup-content-wrapper': {
        backgroundColor: 'rgba(20, 20, 20, 0.95)',
        color: '#eee',
        borderRadius: '2px',
        border: '1px solid #444',
        boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
        fontFamily: 'var(--font-family)',
      },
      '& .leaflet-popup-tip': {
        backgroundColor: 'rgba(20, 20, 20, 0.95)',
        border: '1px solid #444',
        borderTop: 'none',
        borderLeft: 'none',
      }
    }}>
      {/* Control panel overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 1000,
          backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          borderRadius: '2px',
          border: isDarkMode ? '1px solid #444' : '1px solid #ccc',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          padding: '4px',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          maxWidth: 'calc(100% - 20px)',
          gap: 0.5
        }}
      >
        <MuiTooltip describeChild title={t('ui.map.showHistoryHelp')}>
          <Button size="small" startIcon={<HistoryIcon />} aria-pressed={showHistory}
            color={showHistory ? 'primary' : 'inherit'} onClick={() => setShowHistory(value => !value)}>
            {t('ui.map.showHistory')}
          </Button>
        </MuiTooltip>
        {onGeographicAreaChange && <Button size="small" startIcon={<CropFreeIcon />} aria-pressed={drawingArea}
          color={drawingArea ? 'primary' : 'inherit'} onClick={() => setDrawingArea(value => !value)}>
          {t('ui.map.drawArea')}
        </Button>}
        {geographicArea && onGeographicAreaChange && <Button size="small" onClick={() => onGeographicAreaChange(null)}>{t('ui.map.clearArea')}</Button>}
        {onToggleFullscreen && (
          <MuiTooltip title={t(isFullscreen ? 'graph.exitFullscreen' : 'graph.fullscreen')}>
            <IconButton 
              onClick={onToggleFullscreen} 
              size="small"
              sx={{
                color: isDarkMode ? '#fff' : '#333',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                }
              }}
            >
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </MuiTooltip>
        )}
        {onDateChange && (
          <MuiTooltip title={t(minDate && maxDate ? 'graph.timeFilter' : 'graph.map.noTimeData')}>
            <span>
              <IconButton 
                onClick={handleTimeSliderClick} 
                size="small"
                disabled={!minDate || !maxDate}
                sx={{
                  color: isDarkMode ? '#fff' : '#333',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  },
                  '&.Mui-disabled': {
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'
                  }
                }}
              >
                <HourglassBottomIcon fontSize="small" />
              </IconButton>
            </span>
          </MuiTooltip>
        )}
        <MuiTooltip title={t('graph.labelSettings')}>
          <IconButton 
            onClick={handleLabelClick} 
            size="small"
            sx={{
              color: showLabels ? (isDarkMode ? '#fff' : '#333') : '#888',
              '&:hover': {
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <LabelIcon fontSize="small" />
          </IconButton>
        </MuiTooltip>
        <MuiTooltip title={t('graph.documentation')}>
          <IconButton 
            onClick={() => setShowHelp(true)} 
            size="small"
            sx={{
              color: isDarkMode ? '#fff' : '#333',
              '&:hover': {
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </MuiTooltip>
      </Box>

      {filteredCompanyLocations.length === 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.85)' : 'rgba(255, 255, 255, 0.85)',
            padding: 3,
            borderRadius: 2,
            border: isDarkMode ? '1px solid #444' : '1px solid #ccc',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            backdropFilter: 'blur(4px)',
            textAlign: 'center',
            pointerEvents: 'none'
          }}
        >
          <Typography variant="subtitle1" sx={{ color: isDarkMode ? '#fff' : '#222', fontWeight: 600 }}>
            {t('graph.map.noGeographicData')}
          </Typography>
          <Typography variant="body2" sx={{ color: isDarkMode ? '#aaa' : '#666', mt: 1 }}>
            {t('graph.map.noCoordinates')}
          </Typography>
        </Box>
      )}

      <Popover
        id={timeSliderId}
        open={openTimeSlider}
        anchorEl={timeSliderAnchorEl}
        onClose={handleTimeSliderClose}
        container={rootRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.85)' : 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.4)',
            overflow: 'visible',
            mt: 1
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {minDate && maxDate && onDateChange && (
            <TimeSliderPanel
              minDate={minDate}
              maxDate={maxDate}
              selectedDate={selectedDate || null}
              onDateChange={onDateChange}
              isDarkMode={isDarkMode}
            />
          )}
        </Box>
      </Popover>

      {/* Label Settings Menu */}
      <Menu
        disablePortal
        anchorEl={labelAnchorEl}
        open={Boolean(labelAnchorEl)}
        onClose={handleLabelClose}
        container={rootRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            color: isDarkMode ? '#fff' : '#333',
            border: isDarkMode ? '1px solid #444' : '1px solid #ccc',
            backdropFilter: 'blur(4px)',
            '& .MuiCheckbox-root': {
              color: '#888',
              '&.Mui-checked': {
                color: '#1976d2',
              }
            }
          }
        }}
      >
        {/* Toggle-all actions */}
        <Box sx={{ px: 2, pt: 1, pb: 0.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="text"
            onClick={() => {
              const nodeVals = Object.values(nodeLabelSettings);
              const edgeVals = Object.values(edgeLabelSettings);
              const turnOn = nodeVals.some(v => !v) || edgeVals.some(v => !v);
              
              const newNodes = Object.keys(nodeLabelSettings).reduce((acc, k) => {
                acc[k as keyof NodeLabelSettings] = turnOn;
                return acc;
              }, {} as NodeLabelSettings);
              
              const newEdges = Object.keys(edgeLabelSettings).reduce((acc, k) => {
                acc[k as keyof EdgeLabelSettings] = turnOn;
                return acc;
              }, {} as EdgeLabelSettings);
              
              setNodeLabelSettings(newNodes);
              setEdgeLabelSettings(newEdges);
              setShowLabels(turnOn);
            }}
            sx={{ textTransform: 'none', color: '#1976d2' }}
          >
            {t('graph.labels.toggleAll')}
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={() => {
              const nodeVals = Object.values(nodeLabelSettings);
              const turnOn = nodeVals.some(v => !v);
              
              const newNodes = Object.keys(nodeLabelSettings).reduce((acc, k) => {
                acc[k as keyof NodeLabelSettings] = turnOn;
                return acc;
              }, {} as NodeLabelSettings);
              
              setNodeLabelSettings(newNodes);
              setShowLabels(true);
            }}
            sx={{ textTransform: 'none', color: '#1976d2' }}
          >
            {t('graph.labels.toggleNodes')}
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={() => {
              const edgeVals = Object.values(edgeLabelSettings);
              const turnOn = edgeVals.some(v => !v);
              
              const newEdges = Object.keys(edgeLabelSettings).reduce((acc, k) => {
                acc[k as keyof EdgeLabelSettings] = turnOn;
                return acc;
              }, {} as EdgeLabelSettings);
              
              setEdgeLabelSettings(newEdges);
              setShowLabels(true);
            }}
            sx={{ textTransform: 'none', color: '#1976d2' }}
          >
            {t('graph.labels.toggleRelationships')}
          </Button>
        </Box>

        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 3, minWidth: 300 }}>
          {/* Node Labels Column */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
              {t('graph.labels.nodes')}
            </Typography>
            <MenuItem onClick={() => handleNodeLabelToggle('showOrganizationNumber')} sx={{ py: 0.5 }}>
              <Checkbox 
                checked={nodeLabelSettings.showOrganizationNumber} 
                size="small"
                sx={{ mr: 1, p: 0.5 }}
              />
              <Typography variant="body2">{t('common.fields.organizationNumber')}</Typography>
            </MenuItem>
            <MenuItem onClick={() => handleNodeLabelToggle('showOrganizationName')} sx={{ py: 0.5 }}>
              <Checkbox 
                checked={nodeLabelSettings.showOrganizationName} 
                size="small"
                sx={{ mr: 1, p: 0.5 }}
              />
              <Typography variant="body2">{t('shortestPath.organizationName')}</Typography>
            </MenuItem>
            <MenuItem onClick={() => handleNodeLabelToggle('showKonkursLikvidation')} sx={{ py: 0.5 }}>
              <Checkbox 
                checked={nodeLabelSettings.showKonkursLikvidation} 
                size="small"
                sx={{ mr: 1, p: 0.5 }}
              />
              <Typography variant="body2">{t('graph.labels.bankruptcyLiquidation')}</Typography>
            </MenuItem>
            <MenuItem onClick={() => handleNodeLabelToggle('showSkuldsaldo')} sx={{ py: 0.5 }}>
              <Checkbox 
                checked={nodeLabelSettings.showSkuldsaldo} 
                size="small"
                sx={{ mr: 1, p: 0.5 }}
              />
              <Typography variant="body2">{t('graph.labels.kfmDebt')}</Typography>
            </MenuItem>
            <MenuItem onClick={() => handleNodeLabelToggle('showAddress')} sx={{ py: 0.5 }}>
              <Checkbox 
                checked={nodeLabelSettings.showAddress} 
                size="small"
                sx={{ mr: 1, p: 0.5 }}
              />
              <Typography variant="body2">{t('graph.address')}</Typography>
            </MenuItem>
          </Box>

          {/* Divider */}
          <Box sx={{ width: '1px', bgcolor: '#444', my: 1 }} />

          {/* Edge Labels Column */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
              {t('graph.labels.relationships')}
            </Typography>
            <MenuItem onClick={() => handleEdgeLabelToggle('showDateRange')} sx={{ py: 0.5 }}>
              <Checkbox 
                checked={edgeLabelSettings.showDateRange} 
                size="small"
                sx={{ mr: 1, p: 0.5 }}
              />
              <Typography variant="body2">{t('graph.labels.dateRange')}</Typography>
            </MenuItem>
            <MenuItem onClick={() => handleEdgeLabelToggle('showDuration')} sx={{ py: 0.5 }}>
              <Checkbox 
                checked={edgeLabelSettings.showDuration} 
                size="small"
                sx={{ mr: 1, p: 0.5 }}
              />
              <Typography variant="body2">{t('assessment.details.duration')}</Typography>
            </MenuItem>
          </Box>
        </Box>
      </Menu>

      <Dialog 
        open={showHelp} 
        onClose={() => setShowHelp(false)}
        maxWidth="lg"
        fullWidth
        container={() => rootRef.current || document.body}
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogContent>
          <Documentation />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHelp(false)}>{t('common.actions.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* StreetViewPopup removed in favor of inline Popup */}

      <MapContainer
        center={mapCenter}
        zoom={10}
        zoomControl={false}
        style={{ height: '100%', width: '100%', background: '#f0f0f0' }}
      >
        <ZoomControl position="bottomright" />
        {onGeographicAreaChange && <MapAreaSelection drawing={drawingArea} area={geographicArea}
          onChange={area => { onGeographicAreaChange(area); setDrawingArea(false); setSelectedEntity(null); setFilterEnabled(false); }}
          onCancel={cancelDrawing} />}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url={cartoBasemapUrl}
        />
        
        {/* Automatically fit bounds to show all markers */}
        {filteredCompanyLocations.length > 0 && <FitBounds companyLocations={filteredCompanyLocations} area={geographicArea} />}
        
        {/* Handle map resizing */}
        <MapResizer />
        
        {/* Handle zoom level changes */}
        <ZoomHandler onZoomChange={setZoomLevel} />

        {/* Handle map events to clear selection on interaction */}
        <MapEventHandler onInteraction={() => setSelectedLocation(null)} />

        {/* Handle flyTo actions */}
        <MapFlyTo 
          target={selectedLocation} 
          onFlyComplete={() => setSelectedLocation(null)}
        />

        {/* Render grouped historical markers */}
        {groupedHistoricalMarkers.map((group, index) => {
          const key = getCoordinateKey(group.coordinates);
          const orderIndices = addressOrderMap.get(key);
          
          const isHovered = hoveredCoordinates && 
            hoveredCoordinates[0] === group.coordinates[0] && 
            hoveredCoordinates[1] === group.coordinates[1];

          return (
            <Marker
              key={`hist-group-${index}`}
              position={group.coordinates}
              icon={createHistoricalIcon(isHovered ? zoomLevel + 4 : zoomLevel, orderIndices)}
              zIndexOffset={isHovered ? 2000 : 1000}
            >
              <Popup autoPan={false}>
                <div style={{ maxHeight: '200px', overflowY: 'auto', paddingRight: '5px' }}>
                  {group.entries.map((entry, i) => (
                    <div key={i} style={{ marginBottom: i < group.entries.length - 1 ? '12px' : '0', borderBottom: i < group.entries.length - 1 ? '1px solid #444' : 'none', paddingBottom: i < group.entries.length - 1 ? '12px' : '0' }}>
                      <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>
                        {entry.companyName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mt: 0.5, fontFamily: 'var(--font-family)' }}>
                        {t('graph.map.historicalAddress')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#ddd', fontSize: '0.85rem' }}>
                        {entry.addressText}
                      </Typography>
                      {formatDateRange(entry.startDate, entry.endDate, t) && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ color: '#aaa', fontFamily: 'var(--font-family)', mr: 1 }}>
                            {t('graph.map.period')}:
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#fff', fontFamily: 'var(--font-family)', fontWeight: 600 }}>
                            {formatDateRange(entry.startDate, entry.endDate, t)}
                          </Typography>
                        </Box>
                      )}
                    </div>
                  ))}
                  <Button 
                    size="small" 
                    variant="outlined" 
                    fullWidth
                    sx={{ mt: 1, color: '#fff', borderColor: '#666', '&:hover': { borderColor: '#888', backgroundColor: 'rgba(255,255,255,0.05)' } }}
                    onClick={() => {
                      setStreetViewLocation({ lat: group.coordinates[0], lng: group.coordinates[1] });
                      setStreetViewOpen(true);
                    }}
                  >
                    {t('graph.streetView')}
                  </Button>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Render group outlines and connectors for current addresses */}
        {markerGroups.map(group => (
          <React.Fragment key={`group-${group.id}`}>
            <Polygon
              positions={group.bounds}
              pathOptions={{
                color: '#888',
                weight: 1,
                fillColor: '#000',
                fillOpacity: 0.1,
                dashArray: '4, 4'
              }}
            />
            <Polyline
              positions={[group.center, group.gridTopCenter]}
              pathOptions={{
                color: '#888',
                weight: 2,
                dashArray: '4, 4'
              }}
            />
          </React.Fragment>
        ))}

        {filteredCompanyLocations.map(location => {
          if (location.isHidden) return null;
          // Sort all addresses by date to create a path
          const sortedAddresses = [
            ...location.historicalAddresses.map(h => ({ ...h, type: 'historical' })),
            { 
              id: location.currentAddress.id, 
              coordinates: location.currentAddress.coordinates, 
              addressText: location.currentAddress.addressText,
              startDate: location.currentAddress.startDate,
              endDate: location.currentAddress.endDate,
              type: 'current' 
            }
          ].sort((a, b) => {
             // Sort by endDate (oldest first)
             // If endDate is missing, it's considered "latest" (infinity)
             const dateA = a.endDate || '9999-12-31';
             const dateB = b.endDate || '9999-12-31';
             return dateA.localeCompare(dateB);
          });

          // Generate segments
          const segments = [];
          for (let i = 0; i < sortedAddresses.length - 1; i++) {
            const start = sortedAddresses[i];
            const end = sortedAddresses[i+1];
            
            // Skip if coordinates are the same
            if (start.coordinates[0] === end.coordinates[0] && start.coordinates[1] === end.coordinates[1]) {
                continue;
            }
            
            segments.push({ start, end, index: i, total: sortedAddresses.length - 1 });
          }

          return (
            <React.Fragment key={location.companyId}>
              {/* Render sequential segments */}
              {segments.map((segment, i) => {
                // Calculate midpoint with offset (60% along the path)
                const offsetFactor = 0.6;
                const midLat = segment.start.coordinates[0] + (segment.end.coordinates[0] - segment.start.coordinates[0]) * offsetFactor;
                const midLng = segment.start.coordinates[1] + (segment.end.coordinates[1] - segment.start.coordinates[1]) * offsetFactor;
                const bearing = calculateBearing(segment.start.coordinates, segment.end.coordinates);
                
                // Calculate opacity based on recency (older = more transparent)
                // Minimum opacity 0.4, max 1.0
                const opacity = 0.4 + (0.6 * ((segment.index + 1) / segment.total));

                return (
                  <React.Fragment key={`seg-${location.companyId}-${i}`}>
                    <Polyline
                      positions={[segment.start.coordinates, segment.end.coordinates]}
                      pathOptions={{
                        color: '#222',
                        weight: 3,
                        opacity: opacity,
                        dashArray: '5, 5'
                      }}
                    >
                      {(edgeLabelSettings.showDateRange || edgeLabelSettings.showDuration) && (
                        <Tooltip permanent direction="center" className="polyline-tooltip">
                          <div style={{ fontSize: '10px', fontWeight: '600', fontFamily: 'var(--font-family)' }}>
                            {formatEdgeLabel(segment.start.startDate, segment.start.endDate, edgeLabelSettings, t)}
                          </div>
                        </Tooltip>
                      )}
                    </Polyline>
                    {/* Arrow marker at offset midpoint */}
                    <Marker 
                      position={[midLat, midLng]} 
                      icon={createArrowIcon(bearing, `rgba(34, 34, 34, ${opacity})`)}
                      zIndexOffset={500}
                    />
                  </React.Fragment>
                );
              })}

              {/* Current address marker (company icon) - render last so it appears on top */}
              {(() => {
                // Find the positioned marker for this company
                const positionedMarker = positionedMarkers.find(
                  m => m.type === 'company' && m.id === location.companyId
                );
                if (!positionedMarker) return null;
                
                const displayPosition = positionedMarker.displayCoordinates;
                const labelText = buildCompanyLabel(location.companyData, location.isSourceCompany, nodeLabelSettings, t);
                
                const key = getCoordinateKey(location.currentAddress.coordinates);
                const orderIndices = addressOrderMap.get(key);
                
                const isHovered = hoveredCoordinates && 
                  hoveredCoordinates[0] === displayPosition[0] && 
                  hoveredCoordinates[1] === displayPosition[1];

                return (
                    <Marker
                      position={displayPosition}
                      icon={createCompanyIcon(location.companyData, location.isSourceCompany, isHovered ? zoomLevel + 4 : zoomLevel, orderIndices)}
                      zIndexOffset={isHovered ? 2000 : 0}
                    >
                      {showLabels && labelText && (
                        <Tooltip permanent direction="bottom" offset={[0, 5]} className="company-tooltip">
                          <div style={{ fontSize: '11px', whiteSpace: 'pre', textAlign: 'center' }}>
                            {labelText}
                          </div>
                        </Tooltip>
                      )}
                      <Popup autoPan={false}>
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', mb: 1 }}>
                            {location.companyName}
                          </Typography>
                          <Divider sx={{ borderColor: '#444', mb: 1 }} />
                          <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mb: 0.5, fontFamily: 'var(--font-family)' }}>
                            {t('graph.map.currentAddress')}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#ddd', fontSize: '0.85rem' }}>
                            {location.currentAddress.addressText}
                          </Typography>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            fullWidth
                            sx={{ mt: 1, color: '#fff', borderColor: '#666', '&:hover': { borderColor: '#888', backgroundColor: 'rgba(255,255,255,0.05)' } }}
                            onClick={() => {
                              setStreetViewLocation({ lat: location.currentAddress.coordinates[0], lng: location.currentAddress.coordinates[1] });
                              setStreetViewOpen(true);
                            }}
                          >
                            {t('graph.streetView')}
                          </Button>
                        </Box>
                      </Popup>
                    </Marker>
                );
              })()}
            </React.Fragment>
          );
        })}
        {streetViewOpen && streetViewLocation && (
          <Popup
            position={streetViewLocation}
            eventHandlers={{
              remove: () => {
                setStreetViewOpen(false);
              },
            }}
            maxWidth={500}
            minWidth={300}
          >
            <div style={{ height: '300px', width: '400px' }}>
              {process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
                <iframe
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps/embed/v1/streetview?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&location=${streetViewLocation.lat},${streetViewLocation.lng}`}
                  title={t('graph.streetView')}
                />
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  {t('graph.map.missingMapsKey')}
                </div>
              )}
            </div>
          </Popup>
        )}
      </MapContainer>
      </Box>
    </Box>
  );
};

// Helper function to format address text
function formatAddress(properties: any, t: TFunction): string {
  const parts: string[] = [];
  
  if (properties.street) parts.push(properties.street);
  if (properties.postal_address) parts.push(properties.postal_address);
  if (properties.postal_code) parts.push(properties.postal_code);
  
  return parts.length > 0 ? parts.join(', ') : t('graph.map.addressUnavailable');
}

// Helper function to format date range for address relationships
function formatDateRange(startDate?: string, endDate?: string, t?: TFunction): string {
  if (!startDate && !endDate) return '';
  
  const formatDate = (date: string) => {
    // Assuming date format is "YYYY-MM-DD"
    const [year, month, day] = date.split('-');
    return `${year}-${month}-${day}`;
  };
  
  if (startDate && endDate) {
    return `${formatDate(startDate)} → ${formatDate(endDate)}`;
  } else if (startDate) {
    return t ? t('graph.map.fromDate', { date: formatDate(startDate) }) : formatDate(startDate);
  } else if (endDate) {
    return t ? t('graph.map.untilDate', { date: formatDate(endDate) }) : formatDate(endDate);
  }
  
  return '';
}

// Helper function to format edge label based on settings
function formatEdgeLabel(startDate: string | undefined, endDate: string | undefined, settings: EdgeLabelSettings, t: TFunction): string {
  const parts: string[] = [];
  
  if (settings.showDateRange) {
    const range = formatDateRange(startDate, endDate, t);
    if (range) parts.push(range);
  }
  
  if (settings.showDuration && startDate && endDate) {
     const start = new Date(startDate);
     const end = new Date(endDate);
     if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
       const diffTime = Math.abs(end.getTime() - start.getTime());
       const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
       const years = Math.floor(diffDays / 365);
       const months = Math.floor((diffDays % 365) / 30);
       
       let duration = '';
       if (years > 0) duration += t('graph.map.yearsShort', { count: years });
       if (months > 0) duration += ` ${t('graph.map.monthsShort', { count: months })}`;
       if (duration === '') duration = t('graph.map.daysShort', { count: diffDays });
       
       parts.push(`(${duration.trim()})`);
     }
  }
  
  return parts.join(' ');
}

// Helper function to build company label similar to OrgGraphVis
function buildCompanyLabel(company: any, isSourceCompany: boolean, settings: NodeLabelSettings, t: TFunction): string {
  const labelParts: string[] = [];

  // 1. Organization Number
  if (settings.showOrganizationNumber) {
    const orgNum = company.properties?.organization_number || company.id;
    if (orgNum) {
      labelParts.push(orgNum);
    }
  }

  // 2. Organization Name
  if (settings.showOrganizationName && company.properties?.organization_name) {
    labelParts.push(company.properties.organization_name);
  }
  
  // 3. Konkurs/Likvidation info
  if (settings.showKonkursLikvidation) {
    const konkursLikvidationParts: string[] = [];
    
    if (company.properties?.konkurs_avslutad) {
      konkursLikvidationParts.push(`${t('assessment.details.statusValues.BankruptcyEnd')}: ${company.properties.konkurs_avslutad}`);
    } else if (company.properties?.konkurs_inledd) {
      konkursLikvidationParts.push(`${t('assessment.details.statusValues.BankruptcyBegin')}: ${company.properties.konkurs_inledd}`);
    }
    
    if (company.properties?.likvidation_avslutad) {
      konkursLikvidationParts.push(`${t('assessment.details.statusValues.LiquidationEnd')}: ${company.properties.likvidation_avslutad}`);
    } else if (company.properties?.likvidation_beslutad) {
      konkursLikvidationParts.push(`${t('assessment.details.statusValues.LiquidationBegin')}: ${company.properties.likvidation_beslutad}`);
    }
    
    if (konkursLikvidationParts.length > 0) {
      labelParts.push(...konkursLikvidationParts);
    }
  }
  
  // 4. Skuldsaldo (debt balance)
  if (settings.showSkuldsaldo) {
    const totalDebt = Number(company.properties?.amal_skuldsaldo_current || 0) + 
                      Number(company.properties?.emal_skuldsaldo_current || 0);
    
    if (totalDebt > 0) {
      labelParts.push('');
      labelParts.push(`${t('assessment.details.totalDebt')}: ${(totalDebt / 1000000).toFixed(1)} MSEK`);
    }
  }

  // 5. Address
  if (settings.showAddress) {
    const address = formatAddress(company.properties, t);
    if (address && address !== t('graph.map.addressUnavailable')) {
      labelParts.push(address);
    }
  }
  
  return labelParts.join('\n').trim();
}

export default CompanyMapCard;
